import { dueDateFor, paymentBreakdown, paymentTermText, type PaymentBreakdown, type PaymentTerms } from './customers';
import { eachDay, isISODate, isWeekend, longDate, shortDate, type ISODate } from './dates';
import { initialsOf } from './shortcodes';
import type { BillingType, EntryStatus } from './types';

/** Header fields of each kind of document (the editor, the edit file and the stored header use the same keys). */
export const DOC_HEADER_KEYS: Record<'stundennachweis' | 'abschlag', readonly string[]> = {
  stundennachweis: [
    'title',
    'customer',
    'customerAddress',
    'customerContact',
    'projectLabel',
    'projectNumber',
    'customerReference',
    'consultant',
    'partner',
    'periodLabel',
    'siteLocation',
    'place',
    'documentDate',
    'providerSignatureName',
    'clientSignatureName',
    'issuerName',
    'issuerAddress',
    'note',
  ],
  abschlag: [
    'title',
    'sequenceLabel',
    'customer',
    'customerAddress',
    'customerContact',
    'projectLabel',
    'projectNumber',
    'contractNote',
    'asOf',
    'documentDate',
    'place',
    'vatPercent',
    'issuerName',
    'issuerAddress',
    'note',
  ],
};
export const DOC_HEADER_DATE_KEYS: ReadonlySet<string> = new Set(['documentDate', 'asOf']);
export const DOC_HEADER_NUMBER_KEYS: ReadonlySet<string> = new Set(['vatPercent']);

/**
 * Header fields of a document as they may be stored (DATA-09, review of 23.10.2026): only the fields of its kind (others
 * are left out), texts up to 2000 characters, dates as real calendar dates, the VAT rate as a number from 0 to 100. The
 * same rule for the editor, the edit file and every other caller of the server.
 */
export function cleanDocHeader(
  kind: 'stundennachweis' | 'abschlag',
  header: Record<string, unknown>,
): { ok: true; header: Record<string, string | number> } | { ok: false; field: string } {
  const allowed = new Set(DOC_HEADER_KEYS[kind]);
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(header)) {
    if (!allowed.has(k) || v === undefined || v === null) continue;
    if (DOC_HEADER_NUMBER_KEYS.has(k)) {
      if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 100) return { ok: false, field: k };
      out[k] = Math.round(v * 100) / 100;
    } else if (DOC_HEADER_DATE_KEYS.has(k)) {
      if (typeof v !== 'string' || !isISODate(v) || Number(v.slice(0, 4)) < 1950 || Number(v.slice(0, 4)) > 2100) return { ok: false, field: k };
      out[k] = v;
    } else if (typeof v === 'string') out[k] = v.slice(0, 2000);
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = String(v);
    else return { ok: false, field: k };
  }
  return { ok: true, header: out };
}

/** A person without a stored short code is shown by the plain initials (never by a changing abbreviation such as "E. Yılmaz"). */
function codeFallback(name: string): string {
  return initialsOf(name) || name;
}

/**
 * Billing document models. The same model feeds the on-screen preview, the Excel export
 * and the frozen snapshot of a finalized document.
 */

export interface SnEntry {
  id: string;
  personId: string;
  personName: string;
  /** short code of the person (HA, HAL): used in the day text with the prefix "code" */
  personCode?: string | null;
  discipline?: string | null;
  workDate: ISODate;
  minutes: number;
  billedMinutes?: number | null;
  description: string;
  billingType: BillingType;
  workLocation?: string | null;
  taskLabel: string;
  /** title of the task without its number (day text: entries without a description, or [Task] prefix) */
  taskTitle?: string;
  status: EntryStatus;
  salesRateCents?: number | null;
}

export interface SnHeader {
  title: string;
  consultant: string;
  customer: string;
  /** address of the customer (lines), taken from the customer data or typed; optional */
  customerAddress?: string;
  /** contact person at the customer; optional */
  customerContact?: string;
  projectLabel: string;
  projectNumber: string;
  /** order or reference number of the client (Bestellnummer, Kundenreferenz); optional */
  customerReference?: string;
  partner: string;
  periodLabel: string;
  siteLocation: string;
  place: string;
  documentDate: ISODate;
  providerSignatureName: string;
  clientSignatureName: string;
  issuerName: string;
  issuerAddress: string;
  note: string;
}

export interface SnSettings {
  layout: 'matrix' | 'list';
  includeTypes: BillingType[];
  personIds: string[] | null;
  showAmounts: boolean;
  language: 'de' | 'en';
  includeUnapproved: boolean;
  dayTextPrefix: 'discipline' | 'name' | 'code' | 'none';
  /** 'fallback': the task title stands in for an entry without a description; 'always': [Task] before the texts */
  dayTextTask?: 'fallback' | 'always';
  /** work locations added in brackets to a sentence of the day text (e.g. "Baustelle"); others are not named */
  dayTextLocations?: string[];
}

export interface SnOverride {
  billedMinutes?: number | null;
  excluded?: boolean;
}

export interface SnPerson {
  id: string;
  name: string;
  code?: string | null;
  discipline: string | null;
  minutes: number;
}

/**
 * Amount of the billable hours of one person at one sales rate. The rate is taken per entry (valid on its
 * work date); a rate that changes within the period gives a second line for the person.
 */
export interface SnAmountLine {
  personId: string;
  name: string;
  /** first and last work date with this rate */
  from: ISODate;
  to: ISODate;
  /** billable hours at this rate */
  minutes: number;
  /** null: no sales rate stored for these hours (the amount is missing, see missingRateMinutes) */
  rateCents: number | null;
  amountCents: number | null;
}

export interface SnDay {
  date: ISODate;
  label: string;
  weekend: boolean;
  holiday: string | null;
  minutes: Record<string, number>;
  totalMinutes: number;
  text: string;
  textIsCustom: boolean;
  generatedText: string;
  /** the text was edited by hand and the entries of the day changed afterwards (to be checked) */
  textOutdated: boolean;
}

export interface SnRow {
  entryId: string;
  date: ISODate;
  personId: string;
  personName: string;
  taskLabel: string;
  minutes: number;
  originalMinutes: number;
  description: string;
  billingType: BillingType;
  location: string | null;
  status: EntryStatus;
  excluded: boolean;
  overridden: boolean;
}

export interface SnModel {
  kind: 'stundennachweis';
  from: ISODate;
  to: ISODate;
  header: SnHeader;
  settings: SnSettings;
  persons: SnPerson[];
  days: SnDay[];
  rows: SnRow[];
  totalMinutes: number;
  /** only with showAmounts (and the right to see sales rates); otherwise empty and null */
  amounts: SnAmountLine[];
  totalAmountCents: number | null;
  /** billable hours without a stored sales rate (not contained in the total amount) */
  missingRateMinutes: number;
  entryCount: number;
  excludedCount: number;
  unapprovedCount: number;
}

export interface BuildSnInput {
  from: ISODate;
  to: ISODate;
  header: SnHeader;
  settings: SnSettings;
  entries: SnEntry[];
  overrides: Record<string, SnOverride>;
  dayNotes: Record<string, string>;
  /** generated day text at the time a day note was written by hand (date -> text) */
  dayNoteBasis?: Record<string, string>;
  holidays?: Map<ISODate, { name: string }>;
}

/**
 * The option of the day text prefix as it is used. The first preview called the short-code option "initials"; settings and
 * documents saved then still carry that word, and any unknown value falls back to the short code (the default).
 */
export function dayTextPrefixOf(value: unknown): SnSettings['dayTextPrefix'] {
  return value === 'discipline' || value === 'name' || value === 'none' ? value : 'code';
}

/** One sentence of a description: single spaces, no trailing period or semicolon (question and exclamation marks stay). */
function normalizeSentence(s: string): string {
  return s.trim().replace(/\s+/g, ' ').replace(/[.;,:\s]+$/, '');
}

function joinSentences(parts: string[]): string {
  return parts.map((p) => (/[.!?]$/.test(p) ? p : `${p}.`)).join(' ');
}

/**
 * Day text of a Stundennachweis from the entries of one day (the office can replace it by hand).
 *
 * - One line per person (prefix "name" or "code": the fixed short code of the person such as HA or HAL, the same
 *   in every document), per discipline (prefix "discipline"; people without a discipline by
 *   name) or per person without a label ("none"). Lines are sorted by their label.
 * - The description of each entry is a sentence; a description of several paragraphs (Alt+Enter) gives one sentence
 *   per paragraph, still on the line of the person. An entry without a description stands for its task (task title),
 *   so every hour on the day is explained, unless the same line already describes that task (A wrote what was
 *   done on the task, B booked on it without a text: the line keeps A's sentence only).
 * - The work location is added in brackets when it is one of `locations` (e.g. "Baustelle", "Beim Auftraggeber");
 *   office and home office are usually not named for the client.
 * - The same sentence appears once per line (two people of one discipline at the same meeting: one sentence);
 *   without labels also once per day.
 * - taskMode "always": the sentences are grouped by task as "[Task] sentence. sentence."
 */
export function composeDayText(
  dayEntries: SnEntry[],
  prefixSetting: SnSettings['dayTextPrefix'],
  locations: readonly string[] = [],
  taskMode: NonNullable<SnSettings['dayTextTask']> = 'fallback',
): string {
  const prefix = dayTextPrefixOf(prefixSetting);
  interface Line {
    label: string;
    sort: string;
    groups: { task: string; sentences: string[] }[];
    seen: Set<string>;
  }
  const lines = new Map<string, Line>();
  const seenDay = new Set<string>();
  const lineKey = (e: SnEntry) => (prefix === 'discipline' ? `d:${e.discipline || e.personName}` : `p:${e.personId}`);
  const taskKey = (e: SnEntry) => (e.taskLabel || e.taskTitle || '').toLowerCase();
  // tasks of each line that some entry describes: there an entry without a description adds no task title
  const described = new Set(dayEntries.filter((e) => normalizeSentence(e.description || '')).map((e) => `${lineKey(e)}|${taskKey(e)}`));
  const ordered = [...dayEntries].sort((a, b) => a.personName.localeCompare(b.personName));
  for (const e of ordered) {
    const task = normalizeSentence(e.taskTitle ?? e.taskLabel ?? '');
    const key = lineKey(e);
    // every paragraph of a description (Alt+Enter) is a sentence of its own; the day text keeps one line per label
    const sentences = (e.description || '').split(/\n+/).map(normalizeSentence).filter(Boolean);
    if (!sentences.length && taskMode === 'fallback' && !described.has(`${key}|${taskKey(e)}`) && task) sentences.push(task);
    const place = e.workLocation?.trim();
    if (sentences.length && place && locations.some((l) => l.trim().toLowerCase() === place.toLowerCase())) sentences[sentences.length - 1] += ` (${place})`;
    const label =
      prefix === 'discipline' ? e.discipline || e.personName : prefix === 'name' ? e.personName : prefix === 'code' ? e.personCode || codeFallback(e.personName) : '';
    const line = lines.get(key) ?? { label, sort: prefix === 'none' ? e.personName : label, groups: [], seen: new Set<string>() };
    lines.set(key, line);
    const groupKey = taskMode === 'always' ? task : '';
    let group = line.groups.find((g) => g.task === groupKey);
    if (!group) {
      group = { task: groupKey, sentences: [] };
      line.groups.push(group);
    }
    for (const sentence of sentences) {
      const dedupe = `${groupKey.toLowerCase()}|${sentence.toLowerCase()}`;
      if (line.seen.has(dedupe) || (prefix === 'none' && seenDay.has(dedupe))) continue;
      line.seen.add(dedupe);
      seenDay.add(dedupe);
      group.sentences.push(sentence);
    }
  }
  return [...lines.values()]
    .sort((a, b) => a.sort.localeCompare(b.sort))
    .map((l) => {
      const body = l.groups
        .filter((g) => g.sentences.length || g.task)
        .map((g) => (taskMode === 'always' ? [g.task ? `[${g.task}]` : '', joinSentences(g.sentences)].filter(Boolean).join(' ') : joinSentences(g.sentences)))
        .filter(Boolean)
        .join(' ');
      return body ? (l.label ? `${l.label}: ${body}` : body) : '';
    })
    .filter(Boolean)
    .join('\n');
}

export function effectiveMinutes(e: SnEntry, o?: SnOverride): number {
  if (o?.billedMinutes !== undefined && o.billedMinutes !== null) return o.billedMinutes;
  if (e.billedMinutes !== undefined && e.billedMinutes !== null) return e.billedMinutes;
  return e.minutes;
}

export function buildSnModel(input: BuildSnInput): SnModel {
  const { settings } = input;
  const lang = settings.language;
  const inScope = input.entries.filter(
    (e) =>
      e.workDate >= input.from &&
      e.workDate <= input.to &&
      settings.includeTypes.includes(e.billingType) &&
      (!settings.personIds || settings.personIds.includes(e.personId)),
  );
  const rows: SnRow[] = [];
  const counted: SnEntry[] = [];
  let excludedCount = 0;
  let unapprovedCount = 0;
  for (const e of inScope) {
    const o = input.overrides[e.id];
    const excluded = !!o?.excluded;
    const minutes = effectiveMinutes(e, o);
    if (e.status !== 'approved' && e.status !== 'billed') unapprovedCount++;
    rows.push({
      entryId: e.id,
      date: e.workDate,
      personId: e.personId,
      personName: e.personName,
      taskLabel: e.taskLabel,
      minutes,
      originalMinutes: e.minutes,
      description: e.description,
      billingType: e.billingType,
      location: e.workLocation ?? null,
      status: e.status,
      excluded,
      overridden: minutes !== e.minutes,
    });
    if (excluded) {
      excludedCount++;
      continue;
    }
    counted.push({ ...e, minutes });
  }
  rows.sort((a, b) => a.date.localeCompare(b.date) || a.personName.localeCompare(b.personName));

  const personMap = new Map<string, SnPerson>();
  for (const e of counted) {
    const p = personMap.get(e.personId) ?? { id: e.personId, name: e.personName, code: e.personCode ?? null, discipline: e.discipline ?? null, minutes: 0 };
    p.minutes += e.minutes;
    personMap.set(e.personId, p);
  }
  const persons = [...personMap.values()].sort((a, b) => a.name.localeCompare(b.name));
  const amounts: SnAmountLine[] = [];
  let totalAmount: number | null = null;
  let missingRateMinutes = 0;
  if (settings.showAmounts) {
    const lines = new Map<string, SnAmountLine>();
    for (const e of counted) {
      if (e.billingType !== 'billable') continue;
      const rate = e.salesRateCents ?? null;
      const key = `${e.personId}|${rate ?? '-'}`;
      const l = lines.get(key) ?? { personId: e.personId, name: e.personName, from: e.workDate, to: e.workDate, minutes: 0, rateCents: rate, amountCents: null };
      l.minutes += e.minutes;
      if (e.workDate < l.from) l.from = e.workDate;
      if (e.workDate > l.to) l.to = e.workDate;
      lines.set(key, l);
    }
    totalAmount = 0;
    for (const l of lines.values()) {
      if (l.rateCents === null) missingRateMinutes += l.minutes;
      else {
        l.amountCents = Math.round((l.minutes / 60) * l.rateCents);
        totalAmount += l.amountCents;
      }
      amounts.push(l);
    }
    amounts.sort((a, b) => a.name.localeCompare(b.name) || a.from.localeCompare(b.from));
  }

  const days: SnDay[] = eachDay(input.from, input.to).map((date) => {
    const dayEntries = counted.filter((e) => e.workDate === date);
    const minutes: Record<string, number> = {};
    for (const e of dayEntries) minutes[e.personId] = (minutes[e.personId] ?? 0) + e.minutes;
    const generated = composeDayText(dayEntries, settings.dayTextPrefix, settings.dayTextLocations ?? [], settings.dayTextTask ?? 'fallback');
    const custom = input.dayNotes[date];
    const basis = input.dayNoteBasis?.[date];
    return {
      date,
      label: longDate(date, lang),
      weekend: isWeekend(date),
      holiday: input.holidays?.get(date)?.name ?? null,
      minutes,
      totalMinutes: dayEntries.reduce((s, e) => s + e.minutes, 0),
      text: custom !== undefined ? custom : generated,
      textIsCustom: custom !== undefined,
      generatedText: generated,
      textOutdated: custom !== undefined && basis !== undefined && basis !== generated,
    };
  });

  return {
    kind: 'stundennachweis',
    from: input.from,
    to: input.to,
    header: input.header,
    settings,
    persons,
    days,
    rows,
    totalMinutes: counted.reduce((s, e) => s + e.minutes, 0),
    amounts,
    totalAmountCents: totalAmount,
    missingRateMinutes,
    entryCount: counted.length,
    excludedCount,
    unapprovedCount,
  };
}

/** Row label of an amount line: the name, with the date range when the rate changed within the period. */
export function amountLineLabel(line: SnAmountLine, lines: readonly SnAmountLine[]): string {
  const several = lines.filter((l) => l.personId === line.personId).length > 1;
  return several ? `${line.name} (${shortDate(line.from)} - ${shortDate(line.to)})` : line.name;
}

/** The model without sales rates and amounts (for users without the right to see sales rates). */
export function withoutAmounts(model: SnModel): SnModel {
  return { ...model, settings: { ...model.settings, showAmounts: false }, amounts: [], totalAmountCents: null, missingRateMinutes: 0 };
}

/* ------------------------------------------------------------------ */
/* Abschlag (progress invoice by HOAI phases)                           */
/* ------------------------------------------------------------------ */

export interface FeePhaseInput {
  key: string;
  label: string;
  feeCents: number;
  weightPercent?: number | null;
}

export interface AbschlagLine {
  key: string;
  label: string;
  weightPercent: number | null;
  feeCents: number;
  percent: number;
  earnedCents: number;
  previousCents: number;
  currentCents: number;
}

export interface AbschlagExtra {
  label: string;
  amountCents: number;
}

export interface AbschlagHeader {
  title: string;
  customer: string;
  /** address of the invoice recipient (lines), from the customer data or typed; optional */
  customerAddress?: string;
  /** contact person at the recipient; optional */
  customerContact?: string;
  projectLabel: string;
  projectNumber: string;
  contractNote: string;
  asOf: ISODate;
  documentDate: ISODate;
  place: string;
  issuerName: string;
  issuerAddress: string;
  sequenceLabel: string;
  vatPercent: number;
  note: string;
}

/**
 * Payment terms of an invoice document and what they mean for its amounts: due date, deductions after the VAT-inclusive
 * total (Nachlass, Sicherheitseinbehalt ...) and the discount for early payment (Skonto). Shown on the document; the
 * invoice in the accounting system stays the legal original.
 */
export interface AbschlagPayment {
  terms: PaymentTerms;
  breakdown: PaymentBreakdown;
  /** document date plus payment days (null: no payment term stated) */
  dueDate: ISODate | null;
  /** last day for the discount for early payment (null: no discount) */
  discountUntil: ISODate | null;
  /** the sentence for the document: due date and discount */
  text: string;
}

export interface AbschlagModel {
  kind: 'abschlag';
  header: AbschlagHeader;
  lines: AbschlagLine[];
  extras: AbschlagExtra[];
  /** payment terms and the amounts they lead to; null or missing when the document states none (older snapshots have no such field) */
  payment?: AbschlagPayment | null;
  totals: {
    feeCents: number;
    earnedCents: number;
    previousCents: number;
    currentCents: number;
    extrasCents: number;
    netCents: number;
    vatCents: number;
    grossCents: number;
  };
  previousDocuments: { number: string | null; status: string; earnedCents: number }[];
}

export function computeAbschlagLines(
  phases: FeePhaseInput[],
  progress: Record<string, number>,
  previous: Record<string, number>,
): AbschlagLine[] {
  return phases.map((p) => {
    const percent = Math.max(0, Math.min(100, progress[p.key] ?? 0));
    const earnedCents = Math.round((p.feeCents * percent) / 100);
    const previousCents = previous[p.key] ?? 0;
    return {
      key: p.key,
      label: p.label,
      weightPercent: p.weightPercent ?? null,
      feeCents: p.feeCents,
      percent,
      earnedCents,
      previousCents,
      currentCents: earnedCents - previousCents,
    };
  });
}

/** True when the terms say anything that changes or explains the payment (days, discount, deduction). */
export function hasPaymentTerms(terms: PaymentTerms | null | undefined): terms is PaymentTerms {
  if (!terms) return false;
  return terms.paymentDays !== null && terms.paymentDays !== undefined
    ? true
    : !!(terms.discountPercent && terms.discountDays) || (terms.adjustments ?? []).some((a) => a.value > 0);
}

export function buildAbschlagModel(
  header: AbschlagHeader,
  lines: AbschlagLine[],
  extras: AbschlagExtra[],
  previousDocuments: AbschlagModel['previousDocuments'],
  payment?: { terms: PaymentTerms | null | undefined; lang?: 'de' | 'en' },
): AbschlagModel {
  const sum = (f: (l: AbschlagLine) => number) => lines.reduce((s, l) => s + f(l), 0);
  const extrasCents = extras.reduce((s, e) => s + e.amountCents, 0);
  const currentCents = sum((l) => l.currentCents);
  const netCents = currentCents + extrasCents;
  const vatCents = Math.round((netCents * (header.vatPercent || 0)) / 100);
  const terms = payment?.terms;
  const paymentView: AbschlagPayment | null = hasPaymentTerms(terms)
    ? {
        terms,
        breakdown: paymentBreakdown(netCents, header.vatPercent || 0, terms),
        dueDate: terms.paymentDays !== null && terms.paymentDays !== undefined ? dueDateFor(header.documentDate, terms.paymentDays) : null,
        discountUntil: terms.discountPercent && terms.discountDays ? dueDateFor(header.documentDate, terms.discountDays) : null,
        text: paymentTermText(terms, header.documentDate, payment?.lang ?? 'de'),
      }
    : null;
  return {
    kind: 'abschlag',
    header,
    lines,
    extras,
    payment: paymentView,
    totals: {
      feeCents: sum((l) => l.feeCents),
      earnedCents: sum((l) => l.earnedCents),
      previousCents: sum((l) => l.previousCents),
      currentCents,
      extrasCents,
      netCents,
      vatCents,
      grossCents: netCents + vatCents,
    },
    previousDocuments,
  };
}
